<template>
    <div class="flex flex-col items-center">
        <div class="w-full flex flex-col items-center space-y-4 mt-6 max-w-2xl">
            <!-- 新規登録セクション -->
            <div class="pt-12 pb-8 px-12 bg-white rounded-lg shadow-md w-full flex flex-col gap-8">
                <!-- エラーメッセージ -->
                <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />
                <div class="flex flex-col gap-4 text-center">
                    <h2 class="text-xl font-bold">新規登録をして利用を開始しましょう</h2>
                    <p class="text-sm">YouTubeでログインしているアカウントで登録してください。</p>
                </div>
                <button @click="handleRegister" class="flex justify-center btn-secondary btn-lg w-full text-center">
                    <img :src="googleIcon" alt="Google icon" class="mr-2 w-4">Googleで登録する
                </button>
            </div>

            <!-- ログインセクション -->
            <div class="pt-12 pb-8 px-12 bg-white rounded-lg shadow-md w-full font-bold space-y-8">
                <h2 class="text-center text-lg">すでにアカウントをお持ちの方</h2>
                <button @click="handleLogin" class="flex justify-center btn-secondary btn-lg w-full text-center">
                    ログインする
                </button>
            </div>

            <div class="w-full px-4 py-4 text-xs">
                livedoor ECHOESは、Google APIから受信した情報を他のアプリに使用および転送する場合、使用制限要件を含む<a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank" class="link-styles">Google APIサービスユーザーデータポリシー</a>を遵守します。
            </div>
        </div>

        <!-- #301: multi分岐のチャンネル選択モーダル (旧実装復活) -->
        <ChannelSelectModal
            v-if="isChannelSelectModalVisible"
            :channels="channelsDataList"
            @confirm="onChannelSelected"
            @close="isChannelSelectModalVisible = false"
        />
    </div>
    </template>

    <script>
    import {
        ref,
        onMounted,
        inject,
    } from 'vue';
    import {
        useStore
    } from 'vuex';
    import {
        googleSdkLoaded
    } from 'vue3-google-login';
    import axios from 'axios';
    import {
        useRouter
    } from 'vue-router';
    import ErrorMessage from './ErrorMessage.vue';
    import ChannelSelectModal from './ChannelSelectModal.vue';
    import googleIcon from '@/assets/google-color-icon.svg';

    export default {
        components: {
            ErrorMessage,
            ChannelSelectModal,
        },
        setup() {
            const store = useStore();
            const router = useRouter();
            const route = useRouter().currentRoute.value;
            const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
            const channelsDataList = ref([]); // APIから取得したチャンネルリスト
            const isChannelSelectModalVisible = ref(false);
            const pendingEmail = ref(null);
            const pendingToken = ref(null);
            const errorMessage = ref(''); // エラーメッセージの状態
            const fetchChannels = inject("fetchChannels");

            // URLクエリパラメータからの期限切れ情報を確認
        onMounted(() => {
            if (route.query.expired === 'token') {
                errorMessage.value = 'セキュリティのため、定期的な再認証が必要です。再度ログインしてください。';
            } else if (route.query.expired === 'jwt') {
                errorMessage.value = 'セッションの有効期限が切れました。再度ログインしてください。';
            }
        });

            const sendAuthRequest = async (data) => {
                try {
                    const response = await axios.post(`${apiEndpoint}/user-auth/`, data, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });
                    return response;
                } catch (error) {
                    errorMessage.value = error.response?.data?.error || 'Authentication failed'; // エラーメッセージをセット
                    console.error("Error in auth request", error);
                }
            };

            const authenticateWithGoogle = async (action) => {
                const response = await performCodeFlow();
                if (!response) return null;
                return await sendAuthRequest({
                    code: response.code || null,
                    action: action,
                });
            };

            // #301: 選択 or 単独チャンネルを Vuex に保存して AccountCreation に遷移
            const commitChannelAndProceed = (channelData) => {
                store.commit('setChannelData', channelData);
                store.commit('setemail', pendingEmail.value);
                store.commit('settoken', pendingToken.value);
                router.push({ name: 'AccountCreation' });
            };

            const handleRegister = async () => {
                try {
                    const googleResponse = await authenticateWithGoogle('register');
                    if (!googleResponse || googleResponse.status !== 200) return;

                    pendingEmail.value = googleResponse.data.email;
                    pendingToken.value = googleResponse.data.token;

                    if (googleResponse.data.type === 'multi') {
                        // 複数チャンネル保有ユーザーはモーダルで選択させる
                        channelsDataList.value = googleResponse.data.channels_data_list;
                        isChannelSelectModalVisible.value = true;
                    } else {
                        // single 分岐: 配列の先頭を単体オブジェクトとして保存
                        // (multi 分岐とデータ型を統一し、参照側のキー取り出しを単純化)
                        const channelData = Array.isArray(googleResponse.data.channels_data_list)
                            ? googleResponse.data.channels_data_list[0]
                            : googleResponse.data.channels_data_list;
                        commitChannelAndProceed(channelData);
                    }
                } catch (error) {
                    console.error("Error in register", error);
                    errorMessage.value = 'Google認証中にエラーが発生しました。再度お試しください。';
                }
            };

            // #301: ChannelSelectModal からの選択結果を受けて遷移
            const onChannelSelected = (channelData) => {
                if (!channelData) {
                    errorMessage.value = 'チャンネルを選択してください。';
                    return;
                }
                isChannelSelectModalVisible.value = false;
                commitChannelAndProceed(channelData);
            };

            const handleLogin = async () => {
                try {
                    const googleResponse = await authenticateWithGoogle('login');
                    if (!googleResponse || googleResponse.status !== 200) return;

                    await store.dispatch('login', {
                        user_id: googleResponse.data.user_id,
                        accessToken: googleResponse.data.accessToken,
                        expiry: googleResponse.data.expiry,
                        guideline: JSON.stringify(googleResponse.data.userConsent),
                        // #301: CS審査ステータスをレスポンスから取り込む
                        applicationStatus: googleResponse.data.applicationStatus,
                        waitForInvitation: googleResponse.data.waitForInvitation,
                        showApprovalAlert: googleResponse.data.showApprovalAlert,
                        hasMembership: googleResponse.data.hasMembership,
                    });

                    const channels = await fetchChannels(googleResponse.data.accessToken);
                    if (channels && channels.length > 0) {
                        router.push({ name: 'ArticleManagement' });
                    } else {
                        router.push({ name: 'ChannelInvitation' });
                    }
                } catch (error) {
                    console.error("Error in login", error);
                    errorMessage.value = 'Google認証中にエラーが発生しました。再度お試しください。';
                }
            };

            // コードフローを実行する関数
            const performCodeFlow = () => {
                return new Promise((resolve, reject) => {
                    try {
                        googleSdkLoaded((google) => {
                            const codeClient = google.accounts.oauth2.initCodeClient({
                                client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                                scope: 'email profile openid https://www.googleapis.com/auth/youtube.readonly',
                                callback: (response) => {
                                    resolve(response);
                                },
                                error_callback: (error) => {
                                    console.error('コードフローエラー:', error);
                                    reject(error);
                                }
                            });

                            codeClient.requestCode();
                        });
                    } catch (e) {
                        console.error('コードフロー初期化エラー:', e);
                        reject(e);
                    }
                });
            };

            return {
                channelsDataList,
                isChannelSelectModalVisible,
                handleRegister,
                handleLogin,
                onChannelSelected,
                errorMessage,
                googleIcon
            }
        }
    };

    </script>
